import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { Spinner } from '@/components'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import { GetReportResponse } from '@/hooks'
import useNavigationGuardStore from '@/stores/useNavigationGuardStore/useNavigationGuardStore'

type UpdateReportButtonProps = {
    report: GetReportResponse
    onSuccess?: (updatedReport: GetReportResponse) => void
    onPendingChange?: (isPending: boolean) => void
}

export const UpdateReportButton: React.FC<UpdateReportButtonProps> = ({
    report,
    onSuccess,
    onPendingChange,
}) => {
    const { t } = useTranslation('translations')
    const { useUpdateReportMutation } = useReportsApi()
    const { mutate: updateReport, isPending } = useUpdateReportMutation()
    const addAlert = useToastStore((state) => state.addAlert)
    const setUnsavedChanges = useNavigationGuardStore(
        (state) => state.setUnsavedChanges
    )

    useEffect(() => {
        onPendingChange?.(isPending)
    }, [isPending, onPendingChange])

    const updateText = isPending
        ? t('reporting.update_report.updating_report_button')
        : t('reporting.update_report.update_report_button')

    const handleUpdateReport = () => {
        const updatePayload = {
            ...report,
            fields: report.fields,
        }

        updateReport(
            {
                customReportId: report.customReportId,
                data: updatePayload,
            },
            {
                onSuccess: () => {
                    addAlert({
                        variant: 'success',
                        title: t('reporting.update_report.success_toast.title'),
                        description: t(
                            'reporting.update_report.success_toast.description',
                            {
                                reportName: report.name,
                            }
                        ),
                    })
                    setUnsavedChanges(false)
                    onSuccess?.(updatePayload)
                },
                onError: () => {
                    addAlert({
                        variant: 'error',
                        title: t('reporting.update_report.error_toast.title'),
                        description: t(
                            'reporting.update_report.error_toast.description'
                        ),
                    })
                },
            }
        )
    }

    return (
        <Button
            name="update-report"
            size="medium"
            variant="solidPrimary"
            onClick={handleUpdateReport}
            disabled={isPending}
            className="z-50 whitespace-nowrap"
            data-testid="button-update-report"
        >
            {updateText}
            {isPending && (
                <span data-testid="loading-state">
                    <Spinner size={16} width={3} />
                </span>
            )}
        </Button>
    )
}
